/**
 * Mobile Bottom Navigation Bar
 *
 * Persistent 4-tab bottom nav for mobile viewports (< lg breakpoint).
 * Provides quick access to Home, Catalog, Cart, and Account.
 * Hidden on desktop — header nav handles navigation there.
 *
 * Accessibility: WCAG 2.1 AA — keyboard navigable, aria-current, visible focus.
 */

import type React from 'react';
import { Link, useLocation } from 'react-router';
import { House, Grid2x2, ShoppingCart, User } from 'lucide-react';
import { useCartStore } from '@/stores/useCartStore';
import { useAuthStore } from '@/stores/useAuthStore';
import { cn } from '@/lib/utils';

export const MobileBottomNav = () => {
    const location = useLocation();
    const { isAuthenticated } = useAuthStore();
    const items = useCartStore((state) => state.items);
    const cartCount = items.reduce((sum, item) => sum + item.quantity, 0);

    const isActive = (path: string) => {
        if (path === '/') return location.pathname === '/';
        return location.pathname.startsWith(path);
    };

    if (/\/(product|checkout)(\/|$)/.test(location.pathname)) return null;

    const accountHref = isAuthenticated ? '/account' : '/login';

    const tabs: Array<{
        label: string;
        icon: React.ComponentType<{ className?: string }>;
        href: string;
        badge?: number;
    }> = [
        { label: 'Home', icon: House, href: '/' },
        { label: 'Catalogue', icon: Grid2x2, href: '/catalogue' },
        { label: 'Cart', icon: ShoppingCart, href: '/cart', badge: cartCount },
        { label: 'Account', icon: User, href: accountHref },
    ];

    return (
        <nav
            aria-label="Mobile navigation"
            className="fixed bottom-0 inset-x-0 z-40 lg:hidden bg-card border-t border-border"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
            <div className="flex items-stretch">
                {tabs.map(({ label, icon: Icon, href, badge }) => {
                    const active = isActive(href === accountHref && !isAuthenticated ? '/account' : href);
                    return (
                        <Link
                            key={label}
                            to={href}
                            aria-label={label}
                            aria-current={active ? 'page' : undefined}
                            className={cn(
                                'flex min-h-[56px] flex-1 flex-col items-center justify-center py-2 gap-0.5 text-xs font-medium transition-colors',
                                'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                                active ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                            )}
                        >
                            <span className="relative">
                                <Icon className="h-6 w-6" aria-hidden="true" />
                                {badge !== undefined && badge > 0 && (
                                    <span
                                        className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground"
                                        aria-hidden="true"
                                    >
                                        {badge > 99 ? '99+' : badge}
                                    </span>
                                )}
                            </span>
                            <span>{label}</span>
                        </Link>
                    );
                })}
            </div>
        </nav>
    );
};

export default MobileBottomNav;
